import { useCallback, useRef, useState } from 'react';

export interface ProofPhoto {
  id: string;
  uri: string;
  timestamp: number;
}

export function useProofPhotos(maxPhotos = 3) {
  const [photos, setPhotos] = useState<ProofPhoto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addPhotoFromFile = useCallback(
    (file: File) => {
      if (!file.type.startsWith('image/')) {
        setError('Please choose a photo from your camera or gallery.');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const uri = e.target?.result as string | undefined;
        if (!uri) {
          setError('Could not read the photo. Please try again.');
          return;
        }

        setPhotos((prev) => {
          if (prev.length >= maxPhotos) return prev;
          return [
            ...prev,
            {
              id: `photo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              uri,
              timestamp: Date.now(),
            },
          ];
        });
        setError(null);
      };
      reader.onerror = () => setError('Could not read the photo. Please try again.');
      reader.readAsDataURL(file);
    },
    [maxPhotos]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) addPhotoFromFile(file);
      e.target.value = '';
    },
    [addPhotoFromFile]
  );

  const openPicker = useCallback(() => {
    if (photos.length >= maxPhotos) return;
    fileInputRef.current?.click();
  }, [photos.length, maxPhotos]);

  const deletePhoto = useCallback((id: string) => {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
    setError(null);
  }, []);

  return {
    photos,
    error,
    fileInputRef,
    handleFileChange,
    openPicker,
    deletePhoto,
  };
}
